import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  formatUpdateDate,
  getUpdateContentLanguage,
  getUpdateLabels,
  getUpdateNote,
  isPrimaryUpdateLanguage,
  updateNotes,
} from "../updateContent";
import {
  getMicrosoftStoreUrl,
  trackMicrosoftStoreClick,
} from "../utils/microsoftStore";
import NotFoundPage from "./NotFoundPage";
import styles from "./UpdateNotesPage.module.css";

const UPDATES_PER_PAGE = 5;

const UpdateNotesPage: React.FC = () => {
  const { lang = "en", slug } = useParams<{ lang: string; slug?: string }>();
  const [searchParams] = useSearchParams();
  const contentLanguage = getUpdateContentLanguage(lang);
  const labels = getUpdateLabels(lang);
  const selectedUpdate = getUpdateNote(slug);
  const titleRef = useRef<HTMLHeadingElement>(null);

  const selectedIndex = selectedUpdate
    ? updateNotes.findIndex((update) => update.slug === selectedUpdate.slug)
    : 0;
  const requestedPage = Number.parseInt(searchParams.get("page") ?? "", 10);
  const selectedPage = Math.floor(Math.max(selectedIndex, 0) / UPDATES_PER_PAGE) + 1;
  const pageCount = Math.ceil(updateNotes.length / UPDATES_PER_PAGE);
  const [currentPage, setCurrentPage] = useState(selectedPage);

  useEffect(() => {
    setCurrentPage(Number.isFinite(requestedPage)
      ? Math.min(Math.max(requestedPage, 1), pageCount)
      : selectedPage);
  }, [requestedPage, pageCount, selectedPage]);

  const visibleUpdates = useMemo(() => {
    const startIndex = (currentPage - 1) * UPDATES_PER_PAGE;
    return updateNotes.slice(startIndex, startIndex + UPDATES_PER_PAGE);
  }, [currentPage]);

  useEffect(() => {
    if (!slug || !selectedUpdate) {
      return;
    }

    titleRef.current?.focus({ preventScroll: true });
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    titleRef.current?.scrollIntoView({
      behavior: prefersReducedMotion ? "auto" : "smooth",
      block: "start",
    });
  }, [selectedUpdate, slug]);

  if (!selectedUpdate) {
    return <NotFoundPage lang={lang} />;
  }

  const translation = selectedUpdate.translations[contentLanguage];
  const latestUpdate = updateNotes[0];
  const isLatest = selectedUpdate.slug === latestUpdate.slug;
  const DetailHeading = slug ? "h1" : "h2";

  const getPageLink = (page: number): string => {
    const firstUpdateOnPage = updateNotes[(page - 1) * UPDATES_PER_PAGE];
    if (page === 1 && firstUpdateOnPage.slug === latestUpdate.slug) {
      return `/${lang}/updates`;
    }

    return `/${lang}/updates/${firstUpdateOnPage.slug}?page=${page}`;
  };

  return (
    <div className={styles.page} lang={contentLanguage}>
      <header className={styles.hero}>
        <p className={styles.eyebrow}>{labels.eyebrow}</p>
        {slug ? <p className={styles.heroTitle}>{labels.title}</p> : <h1>{labels.title}</h1>}
        <p>{labels.lead}</p>
        {!isPrimaryUpdateLanguage(lang) && (
          <p className={styles.fallbackNotice}>{labels.fallback}</p>
        )}
      </header>

      <article className={styles.detail} aria-labelledby="selected-update-title">
        <div className={styles.releaseRail} aria-hidden="true">
          <span>{selectedUpdate.date.slice(0, 4)}</span>
          <strong>{selectedUpdate.date.slice(5, 7)}</strong>
        </div>

        <div className={styles.detailBody}>
          <div className={styles.releaseMeta}>
            {isLatest && <span className={styles.latestBadge}>{labels.latest}</span>}
            <time dateTime={selectedUpdate.date}>
              {formatUpdateDate(selectedUpdate.date, contentLanguage)}
            </time>
            {selectedUpdate.version && (
              <span>
                {labels.version} {selectedUpdate.version}
              </span>
            )}
          </div>

          <DetailHeading id="selected-update-title" className={styles.releaseTitle} ref={titleRef} tabIndex={-1}>
            {translation.title}
          </DetailHeading>
          <p className={styles.releaseLead}>{translation.lead}</p>

          <div className={styles.sections} aria-label={labels.details}>
            {translation.sections.map((section) => (
              <section className={styles.section} key={section.heading}>
                <h3>{section.heading}</h3>
                <ul>
                  {section.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </section>
            ))}
          </div>

          <a
            className={styles.downloadButton}
            href={getMicrosoftStoreUrl("updates_page")}
            onClick={() => trackMicrosoftStoreClick("updates_page")}
            rel="noopener noreferrer"
            target="_blank"
          >
            {labels.download}
          </a>
        </div>
      </article>

      <section className={styles.archive} aria-labelledby="update-archive-title">
        <div className={styles.archiveHeader}>
          <div>
            <p className={styles.archiveLabel}>CHANGELOG</p>
            <h2 id="update-archive-title">{labels.listTitle}</h2>
            <p>{labels.listLead}</p>
          </div>
          <span className={styles.pageStatus} aria-live="polite">
            {labels.page} {currentPage} / {pageCount}
          </span>
        </div>

        <ol className={styles.updateList} start={(currentPage - 1) * UPDATES_PER_PAGE + 1}>
          {visibleUpdates.map((update) => {
            const item = update.translations[contentLanguage];
            const active = update.slug === selectedUpdate.slug;
            return (
              <li key={update.slug}>
                <Link
                  aria-current={active ? "page" : undefined}
                  className={`${styles.updateLink} ${active ? styles.updateLinkActive : ""}`}
                  to={`/${lang}/updates/${update.slug}?page=${currentPage}`}
                >
                  <span className={styles.listDate}>
                    {formatUpdateDate(update.date, contentLanguage)}
                  </span>
                  <span className={styles.listCopy}>
                    <strong>{item.title}</strong>
                    <span>{item.summary}</span>
                  </span>
                  <span className={styles.openMark} aria-hidden="true">
                    ↗
                  </span>
                </Link>
              </li>
            );
          })}
        </ol>

        {pageCount > 1 && (
          <nav className={styles.pagination} aria-label={labels.listTitle}>
            {currentPage > 1 ? (
              <Link to={getPageLink(currentPage - 1)}>← {labels.previous}</Link>
            ) : (
              <span aria-disabled="true">← {labels.previous}</span>
            )}
            <div className={styles.pageNumbers}>
              {Array.from({ length: pageCount }, (_, index) => index + 1).map(
                (page) => (
                  <Link
                    aria-current={page === currentPage ? "page" : undefined}
                    className={page === currentPage ? styles.currentPage : ""}
                    key={page}
                    to={getPageLink(page)}
                  >
                    {page}
                  </Link>
                ),
              )}
            </div>
            {currentPage < pageCount ? (
              <Link to={getPageLink(currentPage + 1)}>{labels.next} →</Link>
            ) : (
              <span aria-disabled="true">{labels.next} →</span>
            )}
          </nav>
        )}
      </section>
    </div>
  );
};

export default UpdateNotesPage;
